import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Api from '@common/services/api';
import { getApiHeaders, getApiUrl } from '@common/services/api/utils';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import { FileInProgress, FileInputValue } from '@common/types/objects';

import { AlertService } from '@common/services/alert';
import { AsyncList } from '@common/components/list';
import SearchBar from '@common/components/search-bar';
import Placeholder from '@common/components/placeholder';
import { getImageBase64 } from '@common/utils/file';
import { createFileInProgress } from '../file/utils';

type UnsplashInputProps = {
  value?: FileInputValue | FileInputValue[] | null;
  onChange: (value: FileInputValue, index?: number) => void;
  processFile?: boolean;
};

export type UnsplashItem = {
  id: string;
  alt_description?: string;
  width?: string;
  height?: string;
  urls: {
    thumb: string;
    small: string;
    regular: string;
    raw: string;
  };
  links: {
    html: string;
    download_location: string;
  };
  user: {
    name: string;
    links: {
      html: string;
    };
  };
};

export const UnsplashInput = ({ value, onChange, processFile = true }: UnsplashInputProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string | null>('');
  const [processingImageId, setProcessingImageId] = useState<string | null>(null);
  const organisationId = useAppSelector(selected).id;

  const triggerUnsplashDownload = async (downloadLocation: string): Promise<string | null> => {
    const query = new URLSearchParams({ url: downloadLocation }).toString();
    const endpoint = `/api/unsplash-download?${query}`;

    const response = await fetch(endpoint, { keepalive: true });
    if (!response.ok) return null;

    const result = await response.json();
    return result?.url || null;
  };

  const handleSelect = async (item: UnsplashItem) => {
    if (processingImageId) return;

    setProcessingImageId(item.id);

    try {
      const renderableImageUrl = item.urls.regular || item.urls.raw;
      const trackedDownloadUrl = await triggerUnsplashDownload(item.links.download_location);
      const uploadImageUrl = trackedDownloadUrl || renderableImageUrl;

      const res = await fetch(renderableImageUrl);
      const blob = await res.blob();
      const f = new File([blob], 'image');
      const fileIndex = (value && Array.isArray(value) && value.length) || 0;

      if (processFile) {
        const { data } = await Api.post<{ data: FileInputValue }>(`/v2/organisations/${organisationId}/files/url`, {
          url: uploadImageUrl,
          is_document: false,
          file_type: 'image',
        });
        data.preview = await getImageBase64(f);
        onChange(data, fileIndex);
      } else {
        const file: FileInProgress = createFileInProgress(f);
        file.preview = await getImageBase64(f);
        onChange(file, fileIndex);
      }
    } catch (err) {
      AlertService.error(t('organisation:track_modal_error'));
    } finally {
      setProcessingImageId(null);
    }
  };

  const handleItemKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    item: UnsplashItem,
  ) => {
    if (processingImageId) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;

    e.preventDefault();
    handleSelect(item);
  };

  const image = ({ item }: { item: UnsplashItem }) => {
    const isProcessing = processingImageId === item.id;

    return (
      <div
        className={`Unsplash__Item__Container ${isProcessing ? 'Unsplash__Item__Container--loading' : ''}`}
        role="button"
        aria-busy={isProcessing}
        aria-disabled={isProcessing}
        tabIndex={0}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => !isProcessing && handleSelect(item)}
        onKeyDown={(e) => handleItemKeyDown(e, item)}
      >
        <img
          className="Unsplash__Content__Item"
          alt={item.alt_description}
          src={item.urls.thumb}
          srcSet={item.urls.small}
          width={`${item.width}px`}
          height={`${item.height}px`}
        />
        <div className="Unsplash__Item__Attribution">
          {t('common:unsplash_attribution_photo_by')}{' '}
          <a
            href={`${item.user.links.html}?utm_source=one-team&utm_medium=referral`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {item.user.name}
          </a>{' '}
          {t('common:unsplash_attribution_on')}{' '}
          <a
            href="https://unsplash.com/?utm_source=one-team&utm_medium=referral"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {t('common:unsplash_attribution_unsplash')}
          </a>
        </div>
        {isProcessing && (
          <div className="Unsplash__Item__Loading">
            {t('common:form_input_file_processing')}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <SearchBar onSearch={setSearch} />
      <div className="Unsplash__Container">
        <AsyncList
          containerClassName="Unsplash__Content"
          renderRow={image}
          data={{
            useCursor: undefined,
            filter: {
              query: search,
            },
            onFetch: async (offset, filter) => {
              const limitPerPage = 30;
              const page = +(offset!) / limitPerPage + 1;

              const query = Api.utils.toQuery({
                page,
                per_page: limitPerPage,
                query: filter!.query,
                orientation: 'landscape',
                collections: filter!.query ? undefined : true,
                id: filter!.query ? undefined : 'htcaqHWwbSw',
              });

              const headers = await getApiHeaders();
              const res = await fetch(`${getApiUrl('/unsplash')}?${query}`, { headers });
              if (!res.ok) {
                AlertService.error(t('organisation:track_modal_error'));
                return {
                  data: [] as UnsplashItem[],
                  meta: { pagination: {} },
                };
              }

              const json = await res.json();
              const { results, total } = json.results ? json : { results: json, total: json.length };
              return {
                data: results,
                meta: {
                  pagination: {
                    offset: +(offset!),
                    total_count: total,
                    limit: limitPerPage,
                  },
                },
              };
            },
          }}
          placeholder={(
            <Placeholder
              title={t('common:form_input_file_unsplash_placeholder', { context: search && 'search', search })}
            />
          )}
        />
      </div>
    </>
  );
};
